import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import {
  BookOpen,
  Key,
  CreditCard,
  Webhook,
  Shield,
  Gauge,
  AlertTriangle,
  Fingerprint,
  Copy,
  Check,
  Lock,
  Globe,
  Zap,
  ArrowRight,
} from "lucide-react";

// ==================== TYPES ====================

interface CodeBlockProps {
  language: string;
  code: string;
  title?: string;
}

interface DocTab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

// ==================== CODE BLOCK ====================

function CodeBlock({ language, code, title }: CodeBlockProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast({ title: "Copied to clipboard" });
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="rounded-lg border bg-muted/50 overflow-hidden">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/80">
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] font-mono">{language}</Badge>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleCopy}>
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      )}
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ==================== METHOD BADGE ====================

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-blue-500/10 text-blue-600 border-blue-200",
    POST: "bg-green-500/10 text-green-600 border-green-200",
    PATCH: "bg-amber-500/10 text-amber-600 border-amber-200",
    DELETE: "bg-red-500/10 text-red-600 border-red-200",
  };
  return <Badge className={cn("font-mono text-xs", colors[method] || "")}>{method}</Badge>;
}

// ==================== ENDPOINT ROW ====================

function EndpointRow({ method, path, description }: { method: string; path: string; description: string }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-0">
      <MethodBadge method={method} />
      <code className="text-sm font-mono text-foreground flex-shrink-0">{path}</code>
      <span className="text-sm text-muted-foreground ml-auto text-right">{description}</span>
    </div>
  );
}

// ==================== TAB CONFIG ====================

const tabs: DocTab[] = [
  { id: "overview", label: "Overview", icon: BookOpen },
  { id: "authentication", label: "Authentication", icon: Key },
  { id: "cards", label: "Cards API", icon: CreditCard },
  { id: "webhooks", label: "Webhooks", icon: Webhook },
  { id: "errors", label: "Errors & Limits", icon: AlertTriangle },
  { id: "security", label: "Security", icon: Shield },
];

// ==================== CONTENT SECTIONS ====================

function OverviewContent() {
  return (
    <div className="space-y-6">
      {/* Quick Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Base URL</span>
            </div>
            <code className="text-xs text-muted-foreground">https://api.payprism.io/api/v1</code>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Lock className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Auth Method</span>
            </div>
            <code className="text-xs text-muted-foreground">X-API-Key header</code>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">Rate Limit</span>
            </div>
            <code className="text-xs text-muted-foreground">Per-key configurable</code>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Getting Started</CardTitle>
          <CardDescription>
            The VedaLogic Partner API lets you programmatically issue and manage virtual cards,
            check balances, and receive real-time notifications via webhooks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Quick Start</h3>
            <div className="space-y-2">
              {[
                { step: "1", text: "Create an API key from the API Keys page", link: "/developer/api-keys" },
                { step: "2", text: "Copy your key — it's only shown once" },
                { step: "3", text: "Make your first API call using the key in the X-API-Key header" },
                { step: "4", text: "Set up webhooks to receive real-time card event notifications", link: "/developer/webhooks" },
              ].map((item) => (
                <div key={item.step} className="flex items-start gap-3">
                  <div className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                    {item.step}
                  </div>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Available Scopes</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { scope: "cards:issue", desc: "Issue new virtual cards" },
                { scope: "cards:read", desc: "Read card details and balances" },
                { scope: "cards:manage", desc: "Freeze, unfreeze, cancel cards" },
                { scope: "transactions:read", desc: "View transaction history" },
                { scope: "webhooks:read", desc: "List webhook configurations" },
                { scope: "webhooks:manage", desc: "Create and manage webhooks" },
                { scope: "automation:execute", desc: "Submit automation flows and retry tasks" },
                { scope: "automation:read", desc: "Poll task status, list tasks and flows" },
                { scope: "*", desc: "Full access (all scopes)" },
              ].map((s) => (
                <div key={s.scope} className="flex items-center gap-2 border rounded-lg px-3 py-1.5">
                  <code className="text-xs font-mono font-semibold">{s.scope}</code>
                  <span className="text-xs text-muted-foreground">{s.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Endpoints</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Cards</h4>
            <EndpointRow method="POST" path="/api/v1/cards" description="Issue a new virtual card" />
            <EndpointRow method="GET" path="/api/v1/cards" description="List all cards" />
            <EndpointRow method="GET" path="/api/v1/cards/:id" description="Get card details" />
            <EndpointRow method="GET" path="/api/v1/cards/:id/balance" description="Check card balance" />
            <EndpointRow method="PATCH" path="/api/v1/cards/:id" description="Update card details" />
            <EndpointRow method="POST" path="/api/v1/cards/:id/freeze" description="Freeze a card" />
            <EndpointRow method="POST" path="/api/v1/cards/:id/unfreeze" description="Unfreeze a card" />
            <EndpointRow method="POST" path="/api/v1/cards/:id/cancel" description="Cancel a card" />

            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 mt-4">Webhooks</h4>
            <EndpointRow method="POST" path="/api/v1/webhooks" description="Register a webhook" />
            <EndpointRow method="GET" path="/api/v1/webhooks" description="List webhooks" />
            <EndpointRow method="PATCH" path="/api/v1/webhooks/:id" description="Update a webhook" />
            <EndpointRow method="DELETE" path="/api/v1/webhooks/:id" description="Delete a webhook" />
            <EndpointRow method="POST" path="/api/v1/webhooks/:id/test" description="Send a test event" />
            <EndpointRow method="GET" path="/api/v1/webhooks/:id/deliveries" description="View delivery history" />

            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 mt-4">Automation</h4>
            <EndpointRow method="POST" path="/api/v1/partner/automation/flows/execute" description="Execute an automation flow (async)" />
            <EndpointRow method="GET" path="/api/v1/partner/automation/tasks/:id" description="Poll task status" />
            <EndpointRow method="POST" path="/api/v1/partner/automation/tasks/:id/retry" description="Retry a failed task" />
            <EndpointRow method="GET" path="/api/v1/partner/automation/tasks" description="List automation tasks" />
            <EndpointRow method="GET" path="/api/v1/partner/automation/flows" description="List available flows" />
            <EndpointRow method="GET" path="/api/v1/partner/automation/health" description="Check automation health" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AuthenticationContent() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" /> API Key Authentication
          </CardTitle>
          <CardDescription>
            All API requests must include your API key in the <code className="text-xs">X-API-Key</code> header.
            Keys are scoped per-organization and can be restricted by IP, scope, and environment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CodeBlock
            language="bash"
            title="cURL Example"
            code={`curl -X GET https://api.payprism.io/api/v1/cards \\
  -H "X-API-Key: vl_live_abc123..." \\
  -H "Content-Type: application/json"`}
          />

          <CodeBlock
            language="javascript"
            title="Node.js / Fetch"
            code={`const response = await fetch("https://api.payprism.io/api/v1/cards", {
  headers: {
    "X-API-Key": process.env.VEDALOGIC_API_KEY,
    "Content-Type": "application/json",
  },
});

const cards = await response.json();`}
          />

          <CodeBlock
            language="python"
            title="Python / Requests"
            code={`import requests

response = requests.get(
    "https://api.payprism.io/api/v1/cards",
    headers={
        "X-API-Key": os.environ["VEDALOGIC_API_KEY"],
        "Content-Type": "application/json",
    }
)

cards = response.json()`}
          />

          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Keep your API key secret</p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Never expose API keys in client-side code, public repos, or logs.
                  Use environment variables and rotate keys regularly.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Key Prefixes</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-lg p-3">
                <code className="text-xs font-mono font-semibold text-green-600">vl_live_</code>
                <p className="text-xs text-muted-foreground mt-1">Production keys — real card operations</p>
              </div>
              <div className="border rounded-lg p-3">
                <code className="text-xs font-mono font-semibold text-amber-600">vl_test_</code>
                <p className="text-xs text-muted-foreground mt-1">Sandbox keys — safe for testing</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CardsApiContent() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> Issue a Card
          </CardTitle>
          <CardDescription>
            Create a new virtual card. Requires <code className="text-xs">cards:issue</code> scope.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <MethodBadge method="POST" />
            <code className="text-sm font-mono">/api/v1/cards</code>
          </div>

          <CodeBlock
            language="json"
            title="Request Body"
            code={`{
  "customerId": "cust_abc123",
  "type": "virtual",
  "currency": "INR",
  "spendLimit": 50000,
  "spendLimitInterval": "monthly",
  "metadata": {
    "department": "marketing",
    "projectId": "proj_456"
  }
}`}
          />

          <CodeBlock
            language="json"
            title="Response (201 Created)"
            code={`{
  "id": "card_xyz789",
  "customerId": "cust_abc123",
  "type": "virtual",
  "status": "active",
  "last4": "4242",
  "currency": "INR",
  "spendLimit": 50000,
  "spendLimitInterval": "monthly",
  "balance": 0,
  "createdAt": "2025-01-15T10:30:00Z",
  "metadata": {
    "department": "marketing",
    "projectId": "proj_456"
  }
}`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Get Card Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <MethodBadge method="GET" />
            <code className="text-sm font-mono">/api/v1/cards/:id</code>
          </div>

          <CodeBlock
            language="bash"
            title="cURL"
            code={`curl https://api.payprism.io/api/v1/cards/card_xyz789 \\
  -H "X-API-Key: vl_live_abc123..."`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Card Lifecycle</CardTitle>
          <CardDescription>Manage card status through its lifecycle</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { method: "POST", path: "/api/v1/cards/:id/freeze", desc: "Temporarily suspend a card. Transactions will be declined.", scope: "cards:manage" },
            { method: "POST", path: "/api/v1/cards/:id/unfreeze", desc: "Reactivate a frozen card.", scope: "cards:manage" },
            { method: "POST", path: "/api/v1/cards/:id/cancel", desc: "Permanently cancel a card. This cannot be undone.", scope: "cards:manage" },
            { method: "GET", path: "/api/v1/cards/:id/balance", desc: "Check the current balance and available spend.", scope: "cards:read" },
          ].map((ep) => (
            <div key={ep.path + ep.method} className="border rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-2">
                <MethodBadge method={ep.method} />
                <code className="text-sm font-mono">{ep.path}</code>
                <Badge variant="outline" className="ml-auto text-[10px]">{ep.scope}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{ep.desc}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function WebhooksContent() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" /> Webhook Events
          </CardTitle>
          <CardDescription>
            Receive real-time notifications when card events occur. Webhook payloads are signed with
            HMAC-SHA256 for verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Available Events</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { event: "card.created", desc: "A new card was issued" },
                { event: "card.updated", desc: "Card details were modified" },
                { event: "card.frozen", desc: "A card was frozen" },
                { event: "card.unfrozen", desc: "A frozen card was reactivated" },
                { event: "card.cancelled", desc: "A card was permanently cancelled" },
                { event: "*", desc: "Subscribe to all events" },
              ].map((e) => (
                <div key={e.event} className="flex items-center gap-2 border rounded-lg px-3 py-2">
                  <Badge variant="secondary" className="font-mono text-xs">{e.event}</Badge>
                  <span className="text-xs text-muted-foreground">{e.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <CodeBlock
            language="json"
            title="Webhook Payload Example"
            code={`{
  "id": "evt_abc123",
  "type": "card.created",
  "created": "2025-01-15T10:30:00Z",
  "data": {
    "id": "card_xyz789",
    "customerId": "cust_abc123",
    "type": "virtual",
    "status": "active",
    "last4": "4242",
    "currency": "INR"
  }
}`}
          />

          <div className="space-y-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Fingerprint className="h-4 w-4" /> Signature Verification
            </h3>
            <p className="text-sm text-muted-foreground">
              Every webhook request includes an <code className="text-xs">X-Webhook-Signature</code> header
              containing an HMAC-SHA256 signature of the request body, signed with your webhook secret.
            </p>
            <CodeBlock
              language="javascript"
              title="Verify Signature (Node.js)"
              code={`const crypto = require("crypto");

function verifyWebhookSignature(payload, signature, secret) {
  const expected = crypto
    .createHmac("sha256", secret)
    .update(payload, "utf8")
    .digest("hex");

  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

// In your webhook handler:
app.post("/webhooks/vedalogic", (req, res) => {
  const signature = req.headers["x-webhook-signature"];
  const isValid = verifyWebhookSignature(
    JSON.stringify(req.body),
    signature,
    process.env.WEBHOOK_SECRET
  );

  if (!isValid) {
    return res.status(401).json({ error: "Invalid signature" });
  }

  // Process the event
  const event = req.body;
  console.log("Event type:", event.type);

  res.status(200).json({ received: true });
});`}
            />

            <CodeBlock
              language="python"
              title="Verify Signature (Python)"
              code={`import hmac
import hashlib

def verify_webhook_signature(payload: str, signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode("utf-8"),
        payload.encode("utf-8"),
        hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(signature, expected)`}
            />
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Retry Policy</h3>
            <p className="text-sm text-muted-foreground">
              Failed webhook deliveries are retried up to 5 times with exponential backoff.
              After all attempts fail, the webhook is automatically disabled. You can re-enable
              it from the Webhooks management page.
            </p>
            <div className="grid grid-cols-5 gap-2 mt-2">
              {["Immediate", "1 min", "5 min", "30 min", "2 hours"].map((delay, i) => (
                <div key={i} className="text-center">
                  <div className="text-xs font-medium">Attempt {i + 1}</div>
                  <div className="text-[10px] text-muted-foreground">{delay}</div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorsAndLimitsContent() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" /> Error Responses
          </CardTitle>
          <CardDescription>
            The API uses standard HTTP status codes and returns errors in a consistent JSON format.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CodeBlock
            language="json"
            title="Error Response Format"
            code={`{
  "error": {
    "code": "CARD_NOT_FOUND",
    "message": "The requested card does not exist",
    "status": 404
  }
}`}
          />

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">HTTP Status Codes</h3>
            <div className="space-y-1.5">
              {[
                { code: "200", desc: "Success", color: "text-green-600" },
                { code: "201", desc: "Created — resource successfully created", color: "text-green-600" },
                { code: "400", desc: "Bad Request — invalid parameters or missing required fields", color: "text-amber-600" },
                { code: "401", desc: "Unauthorized — invalid or missing API key", color: "text-red-600" },
                { code: "403", desc: "Forbidden — key lacks required scope or IP not allowed", color: "text-red-600" },
                { code: "404", desc: "Not Found — resource does not exist", color: "text-red-600" },
                { code: "409", desc: "Conflict — duplicate idempotency key with different parameters", color: "text-amber-600" },
                { code: "429", desc: "Too Many Requests — rate limit exceeded", color: "text-amber-600" },
                { code: "500", desc: "Internal Server Error", color: "text-red-600" },
              ].map((s) => (
                <div key={s.code} className="flex items-center gap-3 py-1.5 border-b last:border-0">
                  <Badge variant="outline" className={cn("font-mono text-xs w-12 justify-center", s.color)}>{s.code}</Badge>
                  <span className="text-sm">{s.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Common Error Codes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                "INVALID_API_KEY",
                "INSUFFICIENT_SCOPE",
                "RATE_LIMIT_EXCEEDED",
                "IP_NOT_ALLOWED",
                "CARD_NOT_FOUND",
                "CARD_ALREADY_CANCELLED",
                "CARD_ALREADY_FROZEN",
                "INVALID_AMOUNT",
                "IDEMPOTENCY_CONFLICT",
                "WEBHOOK_URL_INVALID",
              ].map((code) => (
                <code key={code} className="text-xs font-mono border rounded px-2 py-1">{code}</code>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="h-5 w-5" /> Rate Limiting
          </CardTitle>
          <CardDescription>
            Rate limits are applied per API key and can be configured when creating or editing a key.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            When you exceed your rate limit, the API returns a <code className="text-xs">429 Too Many Requests</code> response
            with headers indicating when you can retry.
          </p>

          <div className="space-y-2">
            <h3 className="font-semibold text-sm">Rate Limit Headers</h3>
            <div className="space-y-1.5">
              {[
                { header: "X-RateLimit-Limit", desc: "Maximum requests allowed per window" },
                { header: "X-RateLimit-Remaining", desc: "Requests remaining in current window" },
                { header: "X-RateLimit-Reset", desc: "Unix timestamp when the window resets" },
                { header: "Retry-After", desc: "Seconds until you can retry (on 429 only)" },
              ].map((h) => (
                <div key={h.header} className="flex items-start gap-3 py-1.5 border-b last:border-0">
                  <code className="text-xs font-mono font-semibold text-muted-foreground shrink-0">{h.header}</code>
                  <span className="text-sm">{h.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <CodeBlock
            language="javascript"
            title="Handle Rate Limits"
            code={`async function apiCallWithRetry(url, options, maxRetries = 3) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const response = await fetch(url, options);

    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After");
      const waitMs = (parseInt(retryAfter || "1", 10)) * 1000;
      await new Promise(r => setTimeout(r, waitMs));
      continue;
    }

    return response;
  }
  throw new Error("Max retries exceeded");
}`}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5" /> Idempotency
          </CardTitle>
          <CardDescription>
            Safely retry requests without creating duplicate resources.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Include an <code className="text-xs">Idempotency-Key</code> header with POST requests.
            If the same key is sent again, the API returns the original response instead of creating a duplicate.
            Keys expire after 24 hours.
          </p>

          <CodeBlock
            language="bash"
            title="Example with Idempotency Key"
            code={`curl -X POST https://api.payprism.io/api/v1/cards \\
  -H "X-API-Key: vl_live_abc123..." \\
  -H "Idempotency-Key: unique-request-id-12345" \\
  -H "Content-Type: application/json" \\
  -d '{"customerId": "cust_abc123", "type": "virtual", "currency": "INR"}'`}
          />

          <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-4">
            <div className="flex items-start gap-2">
              <Fingerprint className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Best Practice</p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Use a UUID v4 or a deterministic hash of the request parameters as your idempotency key.
                  Sending the same key with different parameters returns a <code className="text-xs">409 Conflict</code>.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SecurityContent() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" /> Security Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {[
              {
                icon: Key,
                title: "Rotate Keys Regularly",
                desc: "Use the key rotation feature to seamlessly transition to a new key without downtime. The old key is revoked after rotation."
              },
              {
                icon: Globe,
                title: "Restrict by IP",
                desc: "Set an IP allowlist on your API keys to ensure they can only be used from your known server IPs. Supports CIDR notation."
              },
              {
                icon: Lock,
                title: "Use Minimum Scopes",
                desc: "Grant each API key only the scopes it needs. A service that only reads card data should not have cards:issue or cards:manage."
              },
              {
                icon: Shield,
                title: "Use Separate Keys per Environment",
                desc: "Create test keys for development (vl_test_) and live keys for production (vl_live_). Never use production keys in test environments."
              },
              {
                icon: Fingerprint,
                title: "Verify Webhook Signatures",
                desc: "Always validate the X-Webhook-Signature header before processing webhook events. Use constant-time comparison to prevent timing attacks."
              },
              {
                icon: Gauge,
                title: "Handle Rate Limits Gracefully",
                desc: "Implement exponential backoff when receiving 429 responses. Monitor your usage on the API Usage dashboard."
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3 p-3 border rounded-lg">
                <div className="p-2 rounded-lg bg-primary/5">
                  <item.icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold">{item.title}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>IP Allowlist Configuration</CardTitle>
          <CardDescription>Restrict API key usage to specific IPs or CIDR ranges</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CodeBlock
            language="json"
            title="Example: Set IP Allowlist on Key"
            code={`// PATCH /api/v1/admin/partner-keys/:id
{
  "ipAllowlist": [
    "203.0.113.50",
    "198.51.100.0/24",
    "2001:db8::/32"
  ]
}`}
          />
          <p className="text-sm text-muted-foreground">
            Requests from IPs not in the allowlist receive a <code className="text-xs">403 Forbidden</code> response
            with the error code <code className="text-xs">IP_NOT_ALLOWED</code>.
            An empty allowlist means the key can be used from any IP.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================

export default function PartnerApiDocsPage() {
  const [location, setLocation] = useLocation();

  // Parse tab from URL query params
  const urlParams = new URLSearchParams(location.split("?")[1] || "");
  const tabFromUrl = urlParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setLocation(`/developer/docs?tab=${value}`);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex w-full">
        {/* Left Sidebar */}
        <div className="w-64 border-r bg-muted/20 flex flex-col">
          <div className="p-6 border-b">
            <h1 className="text-2xl font-bold tracking-tight">Partner API</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Integration reference guide
            </p>
          </div>

          <TabsList className="flex-col h-auto w-full bg-transparent p-3 gap-1 items-stretch justify-start">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="justify-start gap-3 px-4 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm w-full"
              >
                <tab.icon className="h-4 w-4 shrink-0" />
                <span className="text-left flex-1">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-6xl">
            <TabsContent value="overview" className="mt-0">
              <OverviewContent />
            </TabsContent>

            <TabsContent value="authentication" className="mt-0">
              <AuthenticationContent />
            </TabsContent>

            <TabsContent value="cards" className="mt-0">
              <CardsApiContent />
            </TabsContent>

            <TabsContent value="webhooks" className="mt-0">
              <WebhooksContent />
            </TabsContent>

            <TabsContent value="errors" className="mt-0">
              <ErrorsAndLimitsContent />
            </TabsContent>

            <TabsContent value="security" className="mt-0">
              <SecurityContent />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
